import { useEffect, useState, useRef } from "react";
import { Button, Typography, Card, Box, TextField, Grid } from "@mui/material";
import PageHeader from "src/@core/components/page-header";
import Translations from "src/layouts/components/Translations";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { DefaultPaginationSettings } from "src/constants/general.const";
import { toastError, toastSuccess } from "src/utils/utils";
import DialogEditUser from "src/views/dialog/DialogEditUser";
import DialogConfirmation from "src/views/dialog/DialogConfirmation";
import RoleListTable from "src/views/tables/RoleListTable";
import { useNavigate } from "react-router-dom";

const RolesPage = () => {
  const searchTimeoutRef = useRef();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissionList, setPermissionList] = useState([]);
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    DefaultPaginationSettings.ROWS_PER_PAGE
  );
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const toggleConfirm = (e, user = null) => {
    setConfirmOpen((prev) => !prev);
    setUserToDelete(user);
  };



  const toggleEditDialog = (e, userToEdit = null) => {
    setEditDialogOpen((prev) => !prev);
    setUserToEdit(userToEdit);
  };

  const fetchData = ({ currentPage, pageSize, search }) => {
    setLoading(true);
    const params = { page: currentPage, limit: pageSize, search };

    axiosInstance
      .get(ApiEndPoints.ROLES.list, { params })
      .then((response) => {
        setRoles(response.data?.data?.roles || []);
        setTotalCount(response.data?.data?.totalRecords || 0);
      })
      .catch((error) => toastError(error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData({ currentPage, pageSize, search });
  }, [currentPage, pageSize, search]);

  const fetchPermissionsData = () => {
    setLoading(true);
    axiosInstance
      .get(ApiEndPoints.PERMISSIONS.list)
      .then((response) => {
        const permissionData = response.data.data?.permission;
        const mapped = permissionData.map(({ name, id }) => ({
          name,
          permission_id: id,
          // read: false,
          // write: false,
          // add: false,
          // remove: false,
        }));
        setPermissionList(mapped);
      })
      .catch((error) => {
        toastError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPermissionsData();
  }, []);

  const handleSearchChange = (e) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(e.target.value);
      setCurrentPage(1);
    }, 500);
  };

  const onConfirmDelete = () => {
    axiosInstance
      .delete(ApiEndPoints.ROLES.delete(userToDelete?.id))
      .then((response) => {
        toastSuccess(response.data?.message || "User deleted successfully");
        fetchData({ currentPage, pageSize, search });
        setConfirmOpen(false);
        setUserToDelete(null);
      })
      .catch((error) => {
        toastError(error);
      });
  };

  return (
    <>
      <Grid container spacing={4} padding={2}>
        <Grid item xs={12}>
          <PageHeader
            title={
              <Typography variant="h5">
                <Translations text="Roles" />
              </Typography>
            }
            action={
              <Button variant="contained" color="primary"   onClick={() =>
                navigate("/roles/add", {
                  state: { permissions: permissionList },
                })
              }>
                Add Role
              </Button>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Card>
            <Box
              sx={{
                p: 5,
                pb: 0,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "end",
              }}
            >
              <TextField
                type="search"
                size="small"
                placeholder="Search"
                onChange={handleSearchChange}
              />
            </Box>
            <Box sx={{ p: 5 }}>
              <RoleListTable
                rows={roles}
                loading={loading}
                totalCount={totalCount}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                setPageSize={setPageSize}
                pageSize={pageSize}
                toggleEdit={fetchPermissionsData}
                permissionList={permissionList}
                toggleDelete={toggleConfirm}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <DialogEditUser
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        user={userToEdit}
        onSuccess={() => {
          fetchData({
            currentPage: currentPage,
            pageSize: pageSize,
            search: search,
          });
        }}
      />

      <DialogConfirmation
        open={confirmOpen}
        toggle={toggleConfirm}
        title="Delete Role"
        subtitle="Are you sure you want to delete this role?"
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

export default RolesPage;
